import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { LoadingBlock } from "@/components/Spinner/LoadingBlock";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import {
	AutoCompleteItem as DropdownAutoCompleteItem,
	Dropdown,
} from "@/components/Dropdown/Dropdown";
import { useSnackBar } from "@/utils/useSnackbar";

import {
	useGetHubspotOwnersQuery,
	useGetUserMappingsQuery,
	useCreateUserMappingMutation,
	useDeleteUserMappingMutation,
} from "@/pages/Private/redux/hubspot/hubspot.api";
import { useGetSendersUsersQuery } from "@/pages/Private/redux/admin/admin.api";

// Local state: tracks selected owner per user (no id needed for unsaved mappings)
interface LocalMapping {
	userId: number;
	hubspotOwnerId: string;
}

export const HubspotUserMappingTab: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`company.hubspot.userMapping.${key}`);

	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	const { data: sendersData } = useGetSendersUsersQuery();
	const { data: ownersData } = useGetHubspotOwnersQuery();
	const { data: userMappingsData, isLoading: isLoadingMappings } = useGetUserMappingsQuery();
	const [createUserMapping] = useCreateUserMappingMutation();
	const [deleteUserMapping] = useDeleteUserMappingMutation();

	const [mappings, setMappings] = useState<LocalMapping[]>([]);
	const [isSaving, setIsSaving] = useState(false);

	useEffect(() => {
		if (userMappingsData) {
			setMappings(
				userMappingsData.map((m) => ({ userId: m.userId, hubspotOwnerId: m.hubspotOwnerId }))
			);
		}
	}, [userMappingsData]);

	const ownerOptions: DropdownAutoCompleteItem[] = useMemo(
		() =>
			ownersData?.owners?.map((o) => ({
				id: o.id,
				title: `${o.firstName || ""} ${o.lastName || ""} (${o.email})`.trim(),
			})) || [],
		[ownersData]
	);

	const handleOwnerChange = (userId: number, ownerId: string) => {
		setMappings((prev) => {
			const existing = prev.find((m) => m.userId === userId);

			if (existing) {
				return prev.map((m) => (m.userId === userId ? { ...m, hubspotOwnerId: ownerId } : m));
			}

			return [...prev, { userId, hubspotOwnerId: ownerId }];
		});
	};

	const handleSave = async () => {
		try {
			setIsSaving(true);

			const serverMappings = userMappingsData || [];

			// Find mappings to delete (on server but not in local state, or owner changed)
			const toDelete = serverMappings.filter((sm) => {
				const local = mappings.find((m) => m.userId === sm.userId);

				return !local || local.hubspotOwnerId !== sm.hubspotOwnerId;
			});

			// Find mappings to create (in local state but not on server, or owner changed)
			const toCreate = mappings.filter((m) => {
				const server = serverMappings.find((sm) => sm.userId === m.userId);

				return !server || server.hubspotOwnerId !== m.hubspotOwnerId;
			});

			// Delete removed/changed mappings first
			await Promise.all(toDelete.map((m) => deleteUserMapping(m.id).unwrap()));

			// Create new/changed mappings
			await Promise.all(
				toCreate.map((m) =>
					createUserMapping({ userId: m.userId, hubspotOwnerId: m.hubspotOwnerId }).unwrap()
				)
			);

			handleOpenSnackbar(ts("saved"));
		} catch {
			handleOpenSnackbar(ts("saveError"));
		} finally {
			setIsSaving(false);
		}
	};

	const senders = sendersData?.data || [];

	if (isLoadingMappings) {
		return <LoadingBlock />;
	}

	return (
		<div className="flex flex-col w-full gap-4">
			{renderMessageSnackbar()}
			<div className="text-sm text-gray-600 mb-1">{ts("description")}</div>

			{senders.length === 0 ? (
				<div className="py-6 text-center text-gray-500">{ts("noUsers")}</div>
			) : (
				<div className="flex flex-col gap-2">
					<div className="flex items-center gap-4 py-2 border-b border-gray-200 font-medium text-sm text-gray-600">
						<div className="flex-1">{ts("signalistUser")}</div>
						<div className="flex-1">{ts("hubspotOwner")}</div>
					</div>

					{senders.map((user) => {
						const currentMapping = mappings.find((m) => m.userId === user.id);
						const defaultOwner = currentMapping
							? ownerOptions.find((o) => o.id === currentMapping.hubspotOwnerId)
							: undefined;

						return (
							<div key={user.id} className="flex items-center gap-4 py-2 border-b border-gray-100">
								<div className="flex-1 text-sm">
									{user.firstName} {user.lastName} ({user.email})
								</div>
								<div className="flex-1">
									<Dropdown
										data={ownerOptions}
										defaultValue={defaultOwner}
										floating={true}
										handleSelect={(value) => {
											handleOwnerChange(user.id, value.id as string);
										}}
										label=""
									/>
								</div>
							</div>
						);
					})}
				</div>
			)}

			<div className="flex justify-end mt-4">
				<Button
					color={ButtonColor.ACTION}
					isLoading={isSaving}
					size={ButtonSize.S}
					title={t("basics.save")}
					onClick={handleSave}
				/>
			</div>
		</div>
	);
};
